<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn fab small color="primary" @click="$router.back()">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <span class="ml-3 font-weight-bold">
                {{ isEditMode ? "Edit Campaign" : "Create Campaign" }}
              </span>
            </v-card-title>
            <v-card-text>
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Campaign Details Section -->
                <h4 class="mb-3">Campaign Details</h4>
                <v-row>
                  <v-col md="6">
                    <v-text-field
                      v-model="form.name"
                      outlined
                      :rules="[rules.required]"
                      :error-messages="errors.name"
                    >
                      <template v-slot:label>Campaign Name <span style="color:#f44336;font-weight:bold;">*</span></template>
                    </v-text-field>
                  </v-col>
                  <v-col md="6">
                    <v-text-field
                      v-model="form.title"
                      label="Push Notification Title (Optional)"
                      outlined
                      hint="Used as title for push notifications"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col md="12">
                    <v-textarea
                      v-model="form.message"
                      outlined
                      rows="4"
                      counter="2000"
                      :rules="[rules.required, rules.maxLength(2000)]"
                      :error-messages="errors.message"
                    >
                      <template v-slot:label>Message Content <span style="color:#f44336;font-weight:bold;">*</span></template>
                    </v-textarea>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <!-- Channel Selection Section -->
                <h4 class="mb-3">Select Channels *</h4>
                <v-row>
                  <v-col md="12">
                    <v-chip-group
                      v-model="form.channels"
                      multiple
                      active-class="primary--text"
                    >
                      <v-chip
                        filter
                        outlined
                        value="push"
                        large
                      >
                        <v-icon left>mdi-bell</v-icon>
                        Push Notification
                      </v-chip>
                      <v-chip
                        filter
                        outlined
                        value="sms"
                        large
                      >
                        <v-icon left>mdi-message-text</v-icon>
                        SMS
                      </v-chip>
                      <v-chip
                        filter
                        outlined
                        value="whatsapp"
                        large
                      >
                        <v-icon left>mdi-whatsapp</v-icon>
                        WhatsApp
                      </v-chip>
                    </v-chip-group>
                    <div v-if="form.channels.includes('sms')" class="text-caption orange--text mt-2">
                      <v-icon small color="orange">mdi-information</v-icon>
                      SMS charges may apply based on your MSG91 plan.
                    </div>
                    <div v-if="form.channels.includes('whatsapp')" class="text-caption green--text mt-1">
                      <v-icon small color="green">mdi-information</v-icon>
                      WhatsApp messages require pre-approved templates for promotional content.
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <!-- Schedule Configuration Section -->
                <h4 class="mb-3">Schedule Configuration *</h4>
                <v-row>
                  <v-col md="12">
                    <v-radio-group v-model="form.schedule_type" row>
                      <v-radio label="Send Immediately" value="immediate"></v-radio>
                      <v-radio label="Schedule for Later" value="scheduled"></v-radio>
                      <v-radio label="Recurring" value="recurring"></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>

                <!-- Scheduled Date/Time -->
                <v-row v-if="form.schedule_type === 'scheduled'">
                  <v-col md="4">
                    <v-menu
                      v-model="scheduledDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="scheduledDate"
                          label="Scheduled Date *"
                          prepend-icon="mdi-calendar"
                          readonly
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="scheduledDate"
                        :min="today"
                        @input="scheduledDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col md="4">
                    <v-menu
                      v-model="scheduledTimeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="scheduledTime"
                          label="Scheduled Time *"
                          prepend-icon="mdi-clock-outline"
                          readonly
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                        v-model="scheduledTime"
                        format="24hr"
                        @click:minute="scheduledTimeMenu = false"
                      ></v-time-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <!-- Recurring Configuration -->
                <v-row v-if="form.schedule_type === 'recurring'">
                  <v-col md="3">
                    <v-select
                      v-model="form.recurring_type"
                      :items="recurringTypeOptions"
                      label="Frequency *"
                      outlined
                    ></v-select>
                  </v-col>
                  <v-col md="3">
                    <v-menu
                      v-model="recurringTimeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.recurring_time"
                          label="Time of Day *"
                          prepend-icon="mdi-clock-outline"
                          readonly
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                        v-model="form.recurring_time"
                        format="24hr"
                        @click:minute="recurringTimeMenu = false"
                      ></v-time-picker>
                    </v-menu>
                  </v-col>
                  <v-col md="3" v-if="form.recurring_type === 'weekly'">
                    <v-select
                      v-model="form.recurring_day_of_week"
                      :items="daysOfWeek"
                      label="Day of Week *"
                      outlined
                    ></v-select>
                  </v-col>
                  <v-col md="3" v-if="form.recurring_type === 'monthly'">
                    <v-select
                      v-model="form.recurring_day_of_month"
                      :items="daysOfMonth"
                      label="Day of Month *"
                      outlined
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- Recurring Date Range -->
                <v-row v-if="form.schedule_type === 'recurring'">
                  <v-col md="3">
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.recurring_start_date"
                          label="Start Date (Optional)"
                          prepend-icon="mdi-calendar"
                          readonly
                          outlined
                          clearable
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.recurring_start_date"
                        @input="startDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col md="3">
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.recurring_end_date"
                          label="End Date (Optional)"
                          prepend-icon="mdi-calendar"
                          readonly
                          outlined
                          clearable
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.recurring_end_date"
                        :min="form.recurring_start_date"
                        @input="endDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <!-- Target Audience Preview -->
                <h4 class="mb-3">Target Audience</h4>
                <v-row>
                  <v-col md="12">
                    <v-card outlined class="pa-4">
                      <v-row align="center">
                        <v-col md="6">
                          <div class="text-body-1">
                            <strong>Target:</strong> All Customers
                          </div>
                          <div class="text-caption grey--text">
                            Campaign will be sent to all active customers
                          </div>
                        </v-col>
                        <v-col md="6" class="text-right">
                          <v-btn
                            color="primary"
                            outlined
                            @click="previewAudience"
                            :loading="previewLoading"
                          >
                            <v-icon left>mdi-account-group</v-icon>
                            Preview Audience
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-row v-if="audiencePreview">
                        <v-col md="12">
                          <v-alert type="info" outlined dense>
                            <strong>{{ audiencePreview.total }}</strong> customers will receive this campaign.
                            <br />
                            <span class="text-caption">
                              With Phone: {{ audiencePreview.with_phone }} |
                              With Push Token: {{ audiencePreview.with_fcm_token }}
                            </span>
                          </v-alert>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>

                <v-divider class="my-5"></v-divider>

                <!-- Action Buttons -->
                <v-row>
                  <v-col md="12">
                    <v-btn
                      color="grey"
                      outlined
                      large
                      class="mr-3"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      outlined
                      large
                      class="mr-3"
                      @click="save('draft')"
                      :loading="isSaving && saveType === 'draft'"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save as Draft
                    </v-btn>
                    <v-btn
                      color="success"
                      large
                      @click="save('activate')"
                      :loading="isSaving && saveType === 'activate'"
                    >
                      <v-icon left>mdi-check</v-icon>
                      {{ form.schedule_type === 'immediate' ? 'Save & Send Now' : 'Save & Activate' }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="400" persistent>
        <v-card rounded="xxl">
          <v-card-text class="text-center pa-6">
            <v-icon large color="success" style="font-size: 60px">mdi-check-circle</v-icon>
            <h3 class="mt-4">{{ successMessage }}</h3>
            <v-btn color="success" class="mt-4" @click="goToList">OK</v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "MarketingCampaignCreatePage",
  data() {
    return {
      valid: true,
      isEditMode: false,
      campaignId: null,
      form: {
        name: "",
        title: "",
        message: "",
        channels: [],
        schedule_type: "immediate",
        scheduled_at: null,
        recurring_type: "daily",
        recurring_time: "09:00",
        recurring_day_of_week: 1,
        recurring_day_of_month: 1,
        recurring_start_date: null,
        recurring_end_date: null,
        target_filters: null,
      },
      scheduledDate: null,
      scheduledTime: "09:00",
      scheduledDateMenu: false,
      scheduledTimeMenu: false,
      recurringTimeMenu: false,
      startDateMenu: false,
      endDateMenu: false,
      isSaving: false,
      saveType: null,
      previewLoading: false,
      audiencePreview: null,
      showSuccessDialog: false,
      successMessage: "",
      rules: {
        required: (v) => !!v || "This field is required",
        maxLength: (max) => (v) => (v && v.length <= max) || `Maximum ${max} characters`,
      },
      recurringTypeOptions: [
        { text: "Daily", value: "daily" },
        { text: "Weekly", value: "weekly" },
        { text: "Monthly", value: "monthly" },
      ],
      daysOfWeek: [
        { text: "Sunday", value: 0 },
        { text: "Monday", value: 1 },
        { text: "Tuesday", value: 2 },
        { text: "Wednesday", value: 3 },
        { text: "Thursday", value: 4 },
        { text: "Friday", value: 5 },
        { text: "Saturday", value: 6 },
      ],
      daysOfMonth: Array.from({ length: 28 }, (_, i) => ({
        text: `${i + 1}${this.getOrdinalSuffix(i + 1)}`,
        value: i + 1,
      })),
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    },
  },
  async mounted() {
    this.$store.dispatch("errors/setErrors", {});
    if (this.$route.params.campaignId) {
      this.isEditMode = true;
      this.campaignId = this.$route.params.campaignId;
      await this.loadCampaign();
    }
  },
  methods: {
    getOrdinalSuffix(n) {
      if ([11, 12, 13].includes(n % 100)) return "th";
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    },

    async loadCampaign() {
      try {
        const response = await axios.get(`marketing-campaigns/${this.campaignId}`);
        const campaign = response.data.data;

        this.form = {
          name: campaign.name,
          title: campaign.title,
          message: campaign.message,
          channels: campaign.channels || [],
          schedule_type: campaign.schedule_type,
          recurring_type: campaign.recurring_type || "daily",
          recurring_time: campaign.recurring_time || "09:00",
          recurring_day_of_week: campaign.recurring_day_of_week || 1,
          recurring_day_of_month: campaign.recurring_day_of_month || 1,
          recurring_start_date: campaign.recurring_start_date,
          recurring_end_date: campaign.recurring_end_date,
          target_filters: campaign.target_filters,
        };

        if (campaign.scheduled_at) {
          const dt = new Date(campaign.scheduled_at);
          this.scheduledDate = dt.toISOString().substr(0, 10);
          this.scheduledTime = dt.toTimeString().substr(0, 5);
        }
      } catch (error) {
        console.error("Failed to load campaign:", error);
        alert("Failed to load campaign");
        this.$router.back();
      }
    },

    async previewAudience() {
      this.previewLoading = true;
      try {
        const response = await axios.post("marketing-campaigns/preview-audience", {
          target_filters: this.form.target_filters,
        });
        this.audiencePreview = response.data.data;
      } catch (error) {
        console.error("Failed to preview audience:", error);
      }
      this.previewLoading = false;
    },

    async save(type) {
      if (!this.$refs.form.validate()) return;

      if (this.form.channels.length === 0) {
        if (this.$toast) this.$toast.error("Please select at least one channel");
        return;
      }

      this.saveType = type;
      this.isSaving = true;
      this.$store.dispatch("errors/setErrors", {});

      try {
        // Prepare form data
        const formData = { ...this.form };

        // Set scheduled_at if schedule_type is scheduled
        if (formData.schedule_type === "scheduled" && this.scheduledDate && this.scheduledTime) {
          formData.scheduled_at = `${this.scheduledDate} ${this.scheduledTime}:00`;
        }

        let response;
        if (this.isEditMode) {
          response = await axios.patch(`marketing-campaigns/${this.campaignId}`, formData);
        } else {
          response = await axios.post("marketing-campaigns", formData);
        }

        const campaignId = response.data.data.id;

        // If activating, send the campaign (for immediate) or just mark as active
        if (type === "activate" && formData.schedule_type === "immediate") {
          await axios.post("marketing-campaigns/send", { campaign_id: campaignId });
          this.successMessage = "Campaign created and queued for sending!";
        } else if (type === "activate") {
          this.successMessage = "Campaign activated successfully!";
        } else {
          this.successMessage = "Campaign saved as draft!";
        }

        this.showSuccessDialog = true;
      } catch (error) {
        console.error("Failed to save campaign:", error);
        if (error.response?.data?.errors) {
          this.$store.dispatch("errors/setErrors", error.response.data.errors);
        } else {
          if (this.$toast) this.$toast.error("Failed to save campaign: " + (error.response?.data?.message || error.message));
        }
      }

      this.isSaving = false;
    },

    goToList() {
      this.showSuccessDialog = false;
      this.$router.push("/marketing-campaigns");
    },
  },
};
</script>
